import { type ComponentType } from 'react'
import { NavLink, useNavigate } from 'react-router'
import { useAuthStore } from '@/stores/authStore'
import { useConnectionStore } from '@/stores/connectionStore'
import { useOutdatedAgentsStore } from '@/stores/outdatedAgentsStore'
import { usePendingInvitationsStore } from '@/stores/pendingInvitationsStore'
import { UserDropdown } from './UserDropdown'
import { NotificationDropdown } from './NotificationDropdown'
import {
  ServerIcon,
  CodeBracketIcon,
  UsersIcon,
  UserGroupIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  ComputerDesktopIcon,
  CircleStackIcon,
  ChartBarIcon,
  DocumentTextIcon,
  BoltIcon,
} from '@heroicons/react/24/outline'
import { RiRobot3Line } from 'react-icons/ri'
import { clsx } from 'clsx'
import type { IconType } from 'react-icons'

interface MenuItem {
  name: string
  href: string
  icon: ComponentType<{ className?: string }> | IconType
  description: string
  disabled?: boolean
  adminOnly?: boolean
  hidden?: boolean
  badge?: number
  badgeTitle?: string
  secondaryBadge?: number
  secondaryBadgeTitle?: string
}

interface MenuItemLinkProps {
  item: MenuItem
  collapsed: boolean
  onNavClick: () => void
}

function MenuItemLink({ item, collapsed, onNavClick }: MenuItemLinkProps) {
  const isDisabled = item.disabled

  return (
    <NavLink
      to={item.href || '/'}
      data-testid={`sidebar-menu-${item.name.toLowerCase().replaceAll(/\s+/g, '-')}`}
      data-disabled={isDisabled ? 'true' : 'false'}
      className={({ isActive }) =>
        clsx(
          'flex items-center justify-center rounded-md transition-colors',
          collapsed ? 'p-2' : 'flex-col px-2 py-1.5',
          isDisabled
            ? 'text-gray-400 cursor-not-allowed'
            : isActive
            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
        )
      }
      onClick={(e) => {
        if (isDisabled) {
          e.preventDefault()
        } else {
          onNavClick()
        }
      }}
      title={collapsed ? item.name : item.description}
    >
      <div className="relative">
        <item.icon className={collapsed ? 'w-5 h-5' : 'w-4 h-4'} />
        {/* Primary badge (orange) - e.g., outdated agents */}
        {item.badge !== undefined && item.badge > 0 && (
          <span
            className={clsx(
              'absolute flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-orange-500 px-0.5 text-[9px] font-bold text-white',
              collapsed ? '-top-1.5 -right-1.5' : '-top-1 -right-1.5'
            )}
            data-testid={`sidebar-badge-${item.name.toLowerCase().replaceAll(/\s+/g, '-')}`}
            title={item.badgeTitle || `${item.badge} item${item.badge > 1 ? 's' : ''}`}
          >
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
        {/* Secondary badge (blue) - e.g., pending invitations */}
        {item.secondaryBadge !== undefined && item.secondaryBadge > 0 && (
          <span
            className={clsx(
              'absolute flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-blue-500 px-0.5 text-[9px] font-bold text-white',
              collapsed ? '-bottom-1.5 -right-1.5' : '-bottom-1 -right-1.5'
            )}
            data-testid={`sidebar-badge-secondary-${item.name.toLowerCase().replaceAll(/\s+/g, '-')}`}
            title={item.secondaryBadgeTitle || `${item.secondaryBadge} item${item.secondaryBadge > 1 ? 's' : ''}`}
          >
            {item.secondaryBadge > 99 ? '99+' : item.secondaryBadge}
          </span>
        )}
      </div>
      {!collapsed && (
        <span className="text-[10px] font-medium mt-0.5 text-center leading-tight whitespace-nowrap">{item.name}</span>
      )}
    </NavLink>
  )
}

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  isMobile?: boolean
}

export function Sidebar({ collapsed, onToggle, isMobile = false }: SidebarProps) {
  const { user, logout } = useAuthStore()
  const { activeConnection } = useConnectionStore()
  const { outdatedCount } = useOutdatedAgentsStore()
  const { pendingCount } = usePendingInvitationsStore()
  const navigate = useNavigate()

  // Agent health polling lives in useAgentHealthMonitor, mounted once at
  // MainLayout level so it keeps running regardless of sidebar lifecycle.

  const handleNavClick = () => {
    // On mobile, auto-collapse sidebar after navigation for better space utilization
    if (isMobile && !collapsed) {
      // Small delay to allow navigation to complete
      setTimeout(() => { onToggle() }, 100)
    }
  }

  // Check if the connection is Memcached, MongoDB, ClickHouse, or ScyllaDB (which may have different features)
  const isMemcachedConnection = activeConnection?.type === 'memcached'
  const isMongoDBConnection = activeConnection?.type === 'mongodb'
  const isClickHouseConnection = activeConnection?.type === 'clickhouse'
  const isScyllaDBConnection = activeConnection?.type === 'scylladb'
  const isMilvusConnection = activeConnection?.type === 'milvus'
  const isRedisConnection = activeConnection?.type === 'redis'
  const isRedisClusterConnection = activeConnection?.type === 'redis-cluster'

  const menuItems = [
    {
      name: 'Connections',
      href: '/connections',
      icon: ServerIcon,
      description: 'Manage database connections',
    },
    {
      name: 'Explorer',
      href: activeConnection ? `/explorer/${activeConnection.id}` : '/connections',
      icon: CircleStackIcon,
      description: 'Browse database schema',
      disabled: !activeConnection,
    },
    {
      name: 'Query Editor',
      href: activeConnection ? `/query/${activeConnection.id}` : '/connections',
      icon: CodeBracketIcon,
      description: 'Execute SQL queries',
      disabled: !activeConnection,
    },
    {
      name: 'Users',
      href: '/users',
      icon: UsersIcon,
      description: 'User management',
      adminOnly: true,
    },
    {
      name: 'DB Users',
      href: activeConnection ? `/db-users/${activeConnection.id}` : '/connections',
      icon: UserGroupIcon,
      description: 'Database user management',
      disabled: !activeConnection,
      // Only hide for Memcached (Redis supports ACL users)
      hidden: isMemcachedConnection,
    },
    {
      name: 'Sessions',
      href: activeConnection ? `/sessions/${activeConnection.id}` : '/connections',
      icon: ClockIcon,
      description: 'Active session management',
      disabled: !activeConnection,
      // Only hide for Memcached and Milvus (Redis doesn't support session management)
      hidden: isMemcachedConnection || isMilvusConnection,
    },
    {
      name: 'System',
      href: activeConnection ? `/system/${activeConnection.id}` : '/connections',
      icon: ComputerDesktopIcon,
      description: 'System information and variables',
      disabled: !activeConnection,
      // Now supported for all database types including MongoDB
      hidden: false,
    },
    {
      name: 'Monitoring',
      href: activeConnection ? `/monitoring/${activeConnection.id}` : '/connections',
      icon: ChartBarIcon,
      description: 'Database performance monitoring',
      disabled: !activeConnection,
      // Monitoring is not supported for Memcached, MongoDB, ClickHouse, ScyllaDB, Milvus, Redis, and Redis Cluster.
      hidden: isMemcachedConnection || isMongoDBConnection || isClickHouseConnection || isScyllaDBConnection || isMilvusConnection || isRedisConnection || isRedisClusterConnection,
    },
    {
      name: 'CDC',
      href: activeConnection ? `/cdc/${activeConnection.id}` : '/connections',
      icon: DocumentTextIcon,
      description: 'Change Data Capture management',
      disabled: !activeConnection,
      hidden: !isScyllaDBConnection,
    },
    {
      name: 'Service Levels',
      href: activeConnection ? `/service-levels/${activeConnection.id}` : '/connections',
      icon: BoltIcon,
      description: 'Workload prioritization',
      disabled: !activeConnection,
      hidden: !isScyllaDBConnection,
    },
    {
      name: 'Agents',
      href: '/agents',
      icon: RiRobot3Line,
      description: 'Manage database agents',
      badge: outdatedCount > 0 ? outdatedCount : undefined,
      badgeTitle: outdatedCount > 0 ? `${outdatedCount} outdated agent${outdatedCount > 1 ? 's' : ''}` : undefined,
      secondaryBadge: pendingCount > 0 ? pendingCount : undefined,
      secondaryBadgeTitle: pendingCount > 0 ? `${pendingCount} pending invitation${pendingCount > 1 ? 's' : ''}` : undefined,
    },
  ]

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div
      className={clsx(
        'bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-300',
        // Unified styling for both desktop and mobile - always show sidebar, just collapsed or expanded
        collapsed ? 'w-14' : 'w-[74px]',
        // Mobile-specific positioning
        isMobile && 'fixed inset-y-0 left-0 z-50 md:relative'
      )}
    >
      {/* Header */}
      <div className="flex flex-col items-center py-3 px-2 border-b border-gray-200 dark:border-gray-700">
        <img
          src="/images/mazpin/mazpin-logo.png"
          alt="Mazpin Logo"
          className={collapsed ? "w-6 h-6" : "w-7 h-7"}
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          className="mt-2 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          {collapsed ? (
            <ChevronRightIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          ) : (
            <ChevronLeftIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          )}
        </button>
      </div>

      {/* Active Connection - icon only with tooltip on hover */}
      {activeConnection && (
        <div
          className="flex justify-center py-2 border-b border-gray-200 dark:border-gray-700 group relative"
          role="status"
          aria-label={`Connected to ${activeConnection.name} (${activeConnection.type})`}
          data-testid="sidebar-connection-status"
        >
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" aria-hidden="true" />
          {/* Tooltip for full details */}
          <div className="absolute left-full ml-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50">
            {activeConnection.name} ({activeConnection.type})
            <br />
            {activeConnection.host}:{activeConnection.port}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 px-1" aria-label="Main navigation">
        <div className={clsx('flex flex-col', collapsed ? 'gap-1' : 'gap-0.5')}>
          {menuItems.map((item) => {
            if (item.adminOnly && user?.role !== 'admin') return null
            if (item.hidden) return null
            return (
              <MenuItemLink
                key={item.name}
                item={item}
                collapsed={collapsed}
                onNavClick={handleNavClick}
              />
            )
          })}
        </div>
      </nav>

      {/* Notifications - intentionally always icon-only to maintain compact sidebar design */}
      <div className="border-t border-gray-200 dark:border-gray-700 px-2 pt-2 pb-1">
        <NotificationDropdown collapsed={true} />
      </div>

      {/* User Profile - intentionally always icon-only, dropdown expands on interaction */}
      <div className="px-2 pt-1 pb-2">
        <UserDropdown
          user={user}
          collapsed={true}
          onLogout={handleLogout}
        />
      </div>
    </div>
  )
}
